import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface ContentCreationProps {
  selectedBrand: string | null
}

const ContentCreation: React.FC<ContentCreationProps> = ({ selectedBrand }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingContent, setEditingContent] = useState<any>(null)
  const [selectedStatus, setSelectedStatus] = useState<"all" | "draft" | "ready" | "scheduled" | "published">("all")

  const {
    data: content,
    isLoading,
    refetch,
  } = trpc.contentManagement.listContent.useQuery({
    brandId: selectedBrand || undefined,
    status: selectedStatus === "all" ? undefined : selectedStatus,
    limit: 50,
  })

  // const createContentMutation = trpc.contentManagement.createContent.useMutation(); // TODO: Add to backend
  const updateContentMutation = trpc.contentManagement.updateContent.useMutation()
  // const duplicateContentMutation = trpc.contentManagement.duplicateContent.useMutation(); // TODO: Add to backend

  const handleCreateContent = (contentData: any) => {
    try {
      // TODO: Implement createContent mutation in backend
      // await createContentMutation.mutateAsync(contentData);
      console.log("Create content:", contentData)
      setIsCreateModalOpen(false)
      void refetch()
    } catch (error) {
      console.error("Failed to create content:", error)
    }
  }

  const handleUpdateContent = async (contentId: string, updates: any) => {
    try {
      await updateContentMutation.mutateAsync({ contentId, ...updates })
      setEditingContent(null)
      void refetch()
    } catch (error) {
      console.error("Failed to update content:", error)
    }
  }

  const handleDuplicateContent = (contentId: string) => {
    try {
      // TODO: Implement duplicateContent mutation in backend
      // await duplicateContentMutation.mutateAsync({ contentId });
      console.log("Duplicate content:", contentId)
      void refetch()
    } catch (error) {
      console.error("Failed to duplicate content:", error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold ">Content Creation</h2>
          <p className="">Create and manage your content pieces</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create Content
        </button>
      </div>

      {/* Status Filter */}
      <div className="bg-background p-4 rounded-lg shadow">
        <div className="flex bg-foregroundx rounded-lg p-1">
          {(["all", "draft", "ready", "scheduled", "published"] as const).map((status) => (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
                selectedStatus === status ? "bg-background  shadow-sm" : " hover:"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {content?.map((item: any) => (
          <div key={item.content.id} className="bg-background p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-medium  line-clamp-2">{item.content.title || "Untitled Content"}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {item.brand?.name || "No brand"} •
                  <span
                    className={`ml-1 ${
                      item.content.status === "published"
                        ? "text-green-600"
                        : item.content.status === "scheduled"
                          ? "text-purple-600"
                          : item.content.status === "ready"
                            ? ""
                            : ""
                    }`}
                  >
                    {item.content.status}
                  </span>
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleDuplicateContent(item.content.id)}
                  disabled={false}
                  className="text-gray-400 hover: disabled:opacity-50"
                >
                  📋
                </button>
                <button onClick={() => setEditingContent(item)} className="text-gray-400 hover:">
                  ✏️
                </button>
              </div>
            </div>

            {/* Content Preview */}
            <div className="mb-4">
              <p className="text-primary text-sm line-clamp-4">{item.content.body}</p>
            </div>

            {/* Platform Versions */}
            {item.content.platformVersions && Object.keys(item.content.platformVersions).length > 0 && (
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-2">Platform versions:</div>
                <div className="flex space-x-2">
                  {Object.keys(item.content.platformVersions).map((platform) => (
                    <span key={platform} className="px-2 py-1 bg-foregroundx text-primary text-xs rounded capitalize">
                      {platform}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Scheduling Info */}
            {item.content.scheduledAt && (
              <div className="mb-4 text-xs text-gray-500">
                Scheduled: {new Date(item.content.scheduledAt).toLocaleString()}
              </div>
            )}

            {/* Published Info */}
            {item.content.publishedAt && (
              <div className="mb-4 text-xs text-gray-500">
                Published: {new Date(item.content.publishedAt).toLocaleString()}
              </div>
            )}

            {/* Metadata */}
            <div className="flex justify-between text-xs text-gray-500 mb-4">
              <span>Created: {new Date(item.content.createdAt).toLocaleDateString()}</span>
              <span>Updated: {new Date(item.content.updatedAt).toLocaleDateString()}</span>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={() => setEditingContent(item)}
                className="w-full bg-blue-100 text-blue-700 py-2 px-3 rounded text-sm hover:bg-blue-200 transition-colors"
              >
                Edit Content
              </button>

              {item.content.status === "ready" && (
                <button className="w-full bg-green-100 text-green-700 py-2 px-3 rounded text-sm hover:bg-green-200 transition-colors">
                  Schedule Post
                </button>
              )}

              {item.content.status === "draft" && (
                <button
                  onClick={() => handleUpdateContent(item.content.id, { status: "ready" })}
                  className="w-full bg-purple-100 text-purple-700 py-2 px-3 rounded text-sm hover:bg-purple-200 transition-colors"
                >
                  Mark Ready
                </button>
              )}
            </div>
          </div>
        ))}

        {(!content || content.length === 0) && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">✍️</div>
            <h3 className="text-lg font-medium  mb-2">No content yet</h3>
            <p className=" mb-4">
              {selectedStatus === "all"
                ? "Create your first piece of content to get started."
                : `No content with status "${selectedStatus}" found.`}
            </p>
            {selectedStatus === "all" && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Content
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Content Modal */}
      {isCreateModalOpen && (
        <CreateContentModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateContent}
          isLoading={false}
          selectedBrand={selectedBrand}
        />
      )}

      {/* Edit Content Modal */}
      {editingContent && (
        <EditContentModal
          content={editingContent}
          onClose={() => setEditingContent(null)}
          onSubmit={(updates) => handleUpdateContent(editingContent.content.id, updates)}
          isLoading={updateContentMutation.isLoading}
        />
      )}
    </div>
  )
}

const CreateContentModal: React.FC<{
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
  selectedBrand: string | null
}> = ({ onClose, onSubmit, isLoading, selectedBrand }) => {
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    brandId: selectedBrand || "",
    status: "draft",
    platformVersions: {} as Record<string, string>,
  })
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([])

  const { data: brands } = trpc.contentManagement.listBrands.useQuery()

  const platforms = [
    { id: "linkedin", name: "LinkedIn", icon: "💼" },
    { id: "twitter", name: "Twitter/X", icon: "🐦" },
  ]

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms((prev) => {
      const newPlatforms = prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]

      // Update platform versions
      const newPlatformVersions = { ...formData.platformVersions }
      if (newPlatforms.includes(platform) && !newPlatformVersions[platform]) {
        newPlatformVersions[platform] = formData.body
      } else if (!newPlatforms.includes(platform)) {
        delete newPlatformVersions[platform]
      }

      setFormData((prev) => ({ ...prev, platformVersions: newPlatformVersions }))
      return newPlatforms
    })
  }

  const updatePlatformVersion = (platform: string, content: string) => {
    setFormData((prev) => ({
      ...prev,
      platformVersions: {
        ...prev.platformVersions,
        [platform]: content,
      },
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Create New Content</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  placeholder="Content title for organization"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Brand</label>
                <select
                  value={formData.brandId}
                  onChange={(e) => setFormData({ ...formData, brandId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a brand</option>
                  {brands?.map((brand: any) => (
                    <option key={brand.brand.id} value={brand.brand.id}>
                      {brand.brand.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="ready">Ready to Schedule</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Target Platforms</label>
                <div className="space-y-2">
                  {platforms.map((platform) => (
                    <label key={platform.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(platform.id)}
                        onChange={() => togglePlatform(platform.id)}
                        className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-primary">
                        {platform.icon} {platform.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Main Content</label>
                <textarea
                  required
                  value={formData.body}
                  onChange={(e) => {
                    setFormData({ ...formData, body: e.target.value })
                    // Auto-update platform versions that are empty
                    selectedPlatforms.forEach((platform) => {
                      if (!formData.platformVersions[platform]) {
                        updatePlatformVersion(platform, e.target.value)
                      }
                    })
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  rows={8}
                  placeholder="Write your content here..."
                />
              </div>

              <div className="text-sm text-gray-500">Character count: {formData.body.length}</div>
            </div>
          </div>

          {/* Platform-Specific Versions */}
          {selectedPlatforms.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-primary mb-4">Platform-Specific Versions</h4>
              <div className="space-y-4">
                {selectedPlatforms.map((platform) => {
                  const platformInfo = platforms.find((p) => p.id === platform)
                  return (
                    <div key={platform}>
                      <label className="block text-sm font-medium text-primary mb-1">
                        {platformInfo?.icon} {platformInfo?.name} Version
                      </label>
                      <textarea
                        value={formData.platformVersions[platform] || ""}
                        onChange={(e) => updatePlatformVersion(platform, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                        rows={4}
                        placeholder={`Customize for ${platformInfo?.name}...`}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {platform === "twitter" &&
                          formData.platformVersions[platform] &&
                          `${formData.platformVersions[platform].length}/280 characters`}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Creating..." : "Create Content"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const EditContentModal: React.FC<{
  content: any
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ content, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    title: content.content.title || "",
    body: content.content.body,
    status: content.content.status,
    platformVersions: content.content.platformVersions || {},
  })

  const platforms = [
    { id: "linkedin", name: "LinkedIn", icon: "💼" },
    { id: "twitter", name: "Twitter/X", icon: "🐦" },
  ]

  const selectedPlatforms = Object.keys(formData.platformVersions)

  const togglePlatform = (platform: string) => {
    const newPlatformVersions = { ...formData.platformVersions }
    if (selectedPlatforms.includes(platform)) {
      delete newPlatformVersions[platform]
    } else {
      newPlatformVersions[platform] = formData.body
    }
    setFormData((prev) => ({ ...prev, platformVersions: newPlatformVersions }))
  }

  const updatePlatformVersion = (platform: string, content: string) => {
    setFormData((prev) => ({
      ...prev,
      platformVersions: {
        ...prev.platformVersions,
        [platform]: content,
      },
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-medium  mb-4">Edit Content</h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="ready">Ready to Schedule</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="published">Published</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2">Target Platforms</label>
                <div className="space-y-2">
                  {platforms.map((platform) => (
                    <label key={platform.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPlatforms.includes(platform.id)}
                        onChange={() => togglePlatform(platform.id)}
                        className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-primary">
                        {platform.icon} {platform.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Main Content</label>
                <textarea
                  required
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  rows={8}
                />
              </div>

              <div className="text-sm text-gray-500">Character count: {formData.body.length}</div>
            </div>
          </div>

          {/* Platform-Specific Versions */}
          {selectedPlatforms.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-primary mb-4">Platform-Specific Versions</h4>
              <div className="space-y-4">
                {selectedPlatforms.map((platform) => {
                  const platformInfo = platforms.find((p) => p.id === platform)
                  return (
                    <div key={platform}>
                      <label className="block text-sm font-medium text-primary mb-1">
                        {platformInfo?.icon} {platformInfo?.name} Version
                      </label>
                      <textarea
                        value={formData.platformVersions[platform] || ""}
                        onChange={(e) => updatePlatformVersion(platform, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                        rows={4}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {platform === "twitter" &&
                          formData.platformVersions[platform] &&
                          `${formData.platformVersions[platform].length}/280 characters`}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Updating..." : "Update Content"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ContentCreation
